import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../layouts/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import {
  MapPin, Square, Bed, Bath, Home, Phone, Heart, Share2, ChevronLeft, ChevronRight,
  Building, CheckCircle, Bell, FileText, ArrowRight, Users, Sparkles, ShieldCheck, Clock
} from 'lucide-react';
import axios from 'axios';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../contexts/AuthContext';
import homeIcon from '../assets/img3d/home.png';
import PropertyContactChat from '../components/property/PropertyContactChat';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { isAuthenticated, user, isProfessional } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const isClient = isAuthenticated && user?.role?.toLowerCase() === 'client';
  
  const isPropertyOwner = useMemo(() => {
    if (!isAuthenticated || !isProfessional() || !property || !user) return false;
    const ownerId = property.ownerId || property.owner_id;
    return ownerId === user.id;
  }, [isAuthenticated, property, user, isProfessional]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger le bien', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 0 }).format(price);

  const nextImage = () => { if (property.images?.length > 0) setCurrentImageIndex((prev) => (prev + 1) % property.images.length); };
  const prevImage = () => { if (property.images?.length > 0) setCurrentImageIndex((prev) => prev === 0 ? property.images.length - 1 : prev - 1); };

  const headerConfig = useMemo(() => ({
    icon: homeIcon,
    iconColor: "bg-white",
    title: property ? property.title : "Détails du bien",
    subtitle: property ? `${property.location}, ${property.city}` : "Chargement...",
    secondaryAction: "Retour",
    onSecondaryAction: () => navigate(-1),
    ...(isClient && { primaryAction: "Mon Espace", onPrimaryAction: () => navigate('/client') }),
  }), [property, isClient, navigate]);

  if (loading) return <Layout headerConfig={headerConfig}><div className="flex justify-center h-96 items-center"><div className="animate-spin rounded-full h-12 w-12 border-2 border-[#FF9F68] border-t-transparent"></div></div></Layout>;
  if (!property) return <Layout headerConfig={headerConfig}><div className="text-center py-20"><h2 className="text-2xl font-semibold">Bien non trouvé</h2><Button onClick={() => navigate('/properties')} className="mt-6 bg-[#FF9F68] text-white">Retour</Button></div></Layout>;

  return (
    <Layout headerConfig={headerConfig}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        {/* --- SECTION PRINCIPALE (Photos + Infos) --- */}
        {/* On utilise 'items-stretch' pour forcer les colonnes à la même hauteur */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-stretch">
          
          {/* GAUCHE : GALERIE PHOTOS (Largeur 7/12) */}
          {/* Modification ici : on passe en flex column et h-full pour occuper l'espace */}
          <div className="lg:col-span-7 flex flex-col h-full gap-4">
            {/* Container image principal : on enlève aspect-[4/3] et on ajoute flex-1 h-full */}
            <div className="relative flex-1 h-full min-h-[400px] lg:min-h-0 bg-gray-100 rounded-[2rem] overflow-hidden shadow-lg border border-white/50 group">
              {property.images?.length > 0 ? (
                <>
                  {/* L'image prend 100% de la hauteur et largeur du conteneur parent */}
                  <img src={property.images[currentImageIndex]} alt={property.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {/* Overlay Gradient pour contrôles */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {property.images.length > 1 && (
                    <>
                      <Button variant="ghost" onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full w-12 h-12 p-0 text-white border border-white/30">
                        <ChevronLeft className="h-6 w-6" />
                      </Button>
                      <Button variant="ghost" onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full w-12 h-12 p-0 text-white border border-white/30">
                        <ChevronRight className="h-6 w-6" />
                      </Button>
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-md text-white px-4 py-1 rounded-full text-xs font-medium border border-white/10">
                        {currentImageIndex + 1} / {property.images.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300"><Square className="h-16 w-16" /></div>
              )}

              {/* Actions Flottantes */}
              <div className="absolute top-4 right-4 flex gap-2">
                <Button size="icon" className="bg-white/90 backdrop-blur rounded-full text-gray-700 hover:text-red-500 hover:bg-white shadow-sm" onClick={() => toggleFavorite(id)}>
                  <Heart className={`h-5 w-5 ${isFavorite(id) ? 'fill-red-500 text-red-500' : ''}`} />
                </Button>
                <Button size="icon" className="bg-white/90 backdrop-blur rounded-full text-gray-700 hover:bg-white shadow-sm">
                  <Share2 className="h-5 w-5" />
                </Button>
              </div>
              <Badge className="absolute top-4 left-4 bg-white/90 backdrop-blur text-gray-900 font-bold px-4 py-1.5 rounded-full shadow-sm uppercase tracking-wide text-xs">
                {property.transaction_type === 'sale' ? 'Vente' : 'Location'}
              </Badge>
            </div>

            {/* Thumbnails (restent en bas, hauteur fixe) */}
            {property.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide flex-shrink-0">
                {property.images.map((img, idx) => (
                  <button key={idx} onClick={() => setCurrentImageIndex(idx)} 
                    className={`relative w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden transition-all ${currentImageIndex === idx ? 'ring-2 ring-[#FF9F68] ring-offset-2' : 'opacity-70 hover:opacity-100'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* DROITE : DESCRIPTION & DÉTAILS (Largeur 5/12) */}
          {/* Déjà configuré en flex col h-full, donc il s'étire déjà */}
          <div className="lg:col-span-5 flex flex-col h-full">
            <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm flex-1">
              
              {/* Prix & Titre */}
              <div className="mb-8 border-b border-gray-100 pb-6">
                <div className="flex items-baseline gap-2 mb-2">
                  <h1 className="text-4xl font-extrabold text-[#2D3748] tracking-tight">{formatPrice(property.price)}</h1>
                  {property.transaction_type === 'rent' && <span className="text-lg text-gray-400 font-medium">/ mois</span>}
                </div>
                <h2 className="text-xl font-medium text-gray-600 mb-2 leading-snug">{property.title}</h2>
                <div className="flex items-center text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 mr-1.5 text-[#FF9F68]" />
                  {property.city}
                </div>
              </div>

              {/* Caractéristiques Clés (Grid Design) */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-orange-50/50 p-4 rounded-2xl border border-orange-100 flex flex-col items-center justify-center text-center">
                  <Square className="h-6 w-6 text-[#FF9F68] mb-2" />
                  <span className="text-2xl font-bold text-gray-800">{property.surfaceArea || property.surface_area || '-'}</span>
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Surface (m²)</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 flex flex-col items-center justify-center text-center">
                  <div className="flex items-center gap-1 mb-2">
                    <Bed className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-300">|</span>
                    <Bath className="h-5 w-5 text-gray-400" />
                  </div>
                  <span className="text-xl font-bold text-gray-700">
                    {property.bedrooms} ch • {property.bathrooms} sdb
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Pièces</span>
                </div>
                {/* Autres détails (Année, Étage...) */}
                {(property.year_built || property.floor) && (
                   <div className="col-span-2 bg-gray-50/50 p-3 rounded-xl border border-gray-100 flex justify-around text-sm text-gray-500">
                      {property.year_built && <span>Année : <strong className="text-gray-700">{property.year_built}</strong></span>}
                      {property.floor && <span>Étage : <strong className="text-gray-700">{property.floor}</strong></span>}
                   </div>
                )}
              </div>

              {/* Description Text */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#FF9F68]" /> À propos
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm text-justify">
                  {property.description}
                </p>
              </div>

              {/* Équipements (Chips) */}
              {property.amenities?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#FF9F68]" /> Équipements
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.map((amenity, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium border border-gray-200">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION CONTACT : "L'INVITATION EXCLUSIVE"
            ═══════════════════════════════════════════════════════════════════ */}
        {isClient ? (
          // ... (Le chat client reste identique, c'est déjà très fonctionnel) ...
          <Card className="border border-[#CBD5E0]/50 shadow-sm rounded-2xl overflow-hidden">
             {/* ... contenu chat ... */}
             <CardContent className="p-0">
               <PropertyContactChat propertyId={id} transactionType={property.transaction_type} onSuccess={() => toast({title: 'Envoyé', description: 'Message transmis !'})} />
             </CardContent>
          </Card>
        ) : isPropertyOwner ? (
          // ... (Le bouton dashboard propriétaire reste identique) ...
          <Button onClick={() => navigate(`/professional/leads?propertyId=${id}`)} className="w-full h-16 text-lg bg-[#FF9F68]">Voir les leads</Button>
        ) : (
          /* --- NOUVEAU DESIGN CTA POUR VISITEUR --- */
          <div className="relative overflow-hidden rounded-[2.5rem] bg-[#1A202C] text-white shadow-2xl shadow-gray-400/20">
            
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FF9F68]/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/3 pointer-events-none" />
            
            {/* Grille subtile sur le fond sombre */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 p-10 lg:p-16 items-center">
              
              {/* Gauche : Argumentaire Emotionnel */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-4 leading-tight">
                    Ce bien vous <span className="text-[#FF9F68]">correspond</span> ?<br/>
                    Ne le laissez pas filer.
                  </h2>
                  <p className="text-gray-400 text-lg leading-relaxed max-w-md">
                    En créant votre compte gratuitement, vous débloquez l'accès direct au propriétaire et aux outils de suivi exclusifs.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center group-hover:bg-[#FF9F68] transition-colors duration-300">
                      <ShieldCheck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">Dossier Complet</h4>
                      <p className="text-sm text-gray-400">Accédez aux documents légaux et diagnostics</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center group-hover:bg-[#FF9F68] transition-colors duration-300">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">Prise de RDV Prioritaire</h4>
                      <p className="text-sm text-gray-400">Réservez votre créneau de visite en 2 clics</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Droite : Call to Action Focus */}
              <div className="flex flex-col items-center justify-center bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 text-center shadow-2xl">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF9F68] to-[#EBC7A8] flex items-center justify-center mb-6 shadow-lg shadow-orange-500/20">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Rejoignez la communauté</h3>
                <p className="text-sm text-gray-400 mb-8 max-w-xs">
                  Plus de 10 000 utilisateurs font confiance à AWLO pour leur projet immobilier.
                </p>
                
                <Button
                  onClick={() => navigate(`/register?propertyId=${id}&role=client`)}
                  className="w-full h-14 bg-white text-[#1A202C] hover:bg-gray-100 font-bold text-lg rounded-xl shadow-lg transition-all transform hover:-translate-y-1"
                >
                  Créer mon compte gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <p className="mt-4 text-xs text-gray-500">
                  Déjà membre ? <button onClick={() => navigate('/login')} className="text-[#FF9F68] hover:underline font-medium">Connexion</button>
                </p>
              </div>

            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default PropertyDetailPage;